import React, { useState, useEffect, useCallback } from 'react';
import './WindowControls.css';

const isElectron = !!window.electronAPI?.isElectron;

/**
 * WindowControls Component
 * 
 * Provides minimize, maximize/restore, and close buttons for Electron's frameless window.
 * Only renders when running in Electron (window.electronAPI is available).
 * The maximize button swaps to a restore icon when the window is maximized.
 * 
 * @param {boolean} floating - When true, renders as a fixed overlay in the top-right
 *   corner (used on auth/login pages that don't have a Navbar).
 */
const WindowControls = ({ floating = false }) => {
  const [isMaximized, setIsMaximized] = useState(false);

  useEffect(() => {
    if (!isElectron) return;

    window.electronAPI.windowControls.isMaximized().then(setIsMaximized);

    const cleanup = window.electronAPI.windowControls.onMaximizedChanged?.(setIsMaximized);
    return () => cleanup?.();
  }, []);

  const handleMinimize = useCallback(() => {
    window.electronAPI?.windowControls?.minimize();
  }, []);

  const handleMaximize = useCallback(() => {
    window.electronAPI?.windowControls?.maximize();
  }, []);

  const handleClose = useCallback(() => {
    window.electronAPI?.windowControls?.close();
  }, []);

  if (!isElectron) {
    return null;
  }

  return (
    <div className={`window-controls ${floating ? 'window-controls--floating' : ''}`}>
      <button 
        className="window-controls__btn window-controls__btn--minimize"
        onClick={handleMinimize}
        aria-label="Minimize"
      >
        <svg width="10" height="1" viewBox="0 0 10 1">
          <rect width="10" height="1" fill="currentColor" />
        </svg>
      </button>
      
      <button 
        className="window-controls__btn window-controls__btn--maximize"
        onClick={handleMaximize}
        aria-label={isMaximized ? 'Restore' : 'Maximize'}
      >
        {isMaximized ? (
          <svg width="10" height="10" viewBox="0 0 10 10">
            <path d="M3 0.5h6.5v6.5" fill="none" stroke="currentColor" strokeWidth="1" />
            <rect x="0.5" y="2.5" width="7" height="7" fill="none" stroke="currentColor" strokeWidth="1" />
          </svg>
        ) : (
          <svg width="10" height="10" viewBox="0 0 10 10">
            <rect x="0.5" y="0.5" width="9" height="9" fill="none" stroke="currentColor" strokeWidth="1" />
          </svg>
        )}
      </button>
      
      <button 
        className="window-controls__btn window-controls__btn--close"
        onClick={handleClose}
        aria-label="Close"
      >
        <svg width="10" height="10" viewBox="0 0 10 10">
          <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
};

export default WindowControls;
